/**
 * DemarrageCreationController — assistant `/demarrage-creation.html`
 * (EPIC-008 / 8.7).
 *
 * Câblage backend du parcours A « Créer ma micro-entreprise via Notamicro » :
 *   - **Étape 4** : upload des 3 PJ obligatoires (`identity`, `address`,
 *     `non_condemnation`) via `POST /inpi/creation/attachments`. L'ID
 *     d'attachment est conservé dans le state local du controller pour
 *     être renvoyé au submit.
 *   - **Étape 5** : click `Valider et recevoir le lien de paiement` →
 *     `POST /inpi/creation/submit` avec `{identity, activities,
 *     attachments, consents}` collectés depuis le formulaire. À succès :
 *     bandeau « Lien de paiement envoyé », bouton retour dashboard.
 *
 * **Scope V1 (8.7)** :
 *   - Les pièces complémentaires (RC Pro, décennale, etc.) ne sont pas
 *     wirées en V1 — l'INPI peut les demander en régularisation
 *     (cf. `/inpi-regularisation.html`).
 *   - La navigation du wizard (steps 1-3) repose sur l'inline JS de la
 *     page (livré EPIC-002). Les champs des steps 1-3 sont collectés
 *     au submit via leurs `id` HTML.
 *   - L'enregistrement d'un brouillon (« Enregistrer brouillon ») reste
 *     hors scope V1.
 *
 * Conventions :
 *   - Pas d'HTML inline (mise à jour via `textContent`/`hidden` uniquement).
 *   - Aucune classe CSS inventée.
 *   - Vocabulaire UI : « INPI », « Guichet Unique », « SIRET », « Qonto Pay »
 *     visibles ; pas de NotaFacto/Sweego/SuperPDP.
 */

import BaseController from "./base_controller.js";
import { api } from "/js/services/api.js";
import { COUNTRIES, EU_EEA_COUNTRIES, countryNameByIso3 } from "/js/utils/countries.js";

// Pièces obligatoires (gate du submit). `rcPro` est une PJ complémentaire
// FACULTATIVE (catégorie « Autre », 99.94) : jamais dans le gate, mais incluse
// au payload si l'utilisateur l'a téléversée.
const PJ_TYPES = ["identity", "address", "nonCondemnation"];
const PJ_OPTIONAL_TYPES = ["rcPro"];
// `residence` (titre de séjour, PJ_14) : PJ conditionnelle, exigée uniquement
// pour un prospect hors UE/EEE (99.98) — gate dédié dans submit().
const PJ_API_TYPE = {
  identity: "identity",
  address: "address",
  nonCondemnation: "non_condemnation",
  rcPro: "rc_pro",
  residence: "residence_permit",
};
const PJ_LABEL = {
  identity: "Justificatif d'identité",
  address: "Justificatif de domicile",
  nonCondemnation: "Déclaration de non-condamnation",
  rcPro: "Attestation RC Pro",
  residence: "Titre de séjour",
};

// Régimes identifiés par le quiz d'orientation (99.95). Clé dérivée de
// `category` + axe conditionnel (`is_artisanal` pour bic_services, caisse
// pour bnc). Sert au récapitulatif affiché après résolution du quiz.
const REGIME_RECAP = {
  bic_vente: {
    title: "BIC — vente de marchandises",
    desc: "Abattement de 71 % sur le chiffre d'affaires. Vous vendez des produits ou marchandises.",
  },
  bic_services_commercial: {
    title: "BIC — prestations de services commerciales",
    desc: "Abattement de 50 %. Prestations commerciales ou intellectuelles.",
  },
  bic_services_artisanal: {
    title: "BIC — prestations de services artisanales",
    desc: "Abattement de 50 %. Activité artisanale (immatriculation au répertoire des métiers).",
  },
  bnc_cipav: {
    title: "BNC — profession libérale (CIPAV)",
    desc: "Abattement de 34 %. Retraite gérée par la CIPAV.",
  },
  bnc_ssi: {
    title: "BNC — profession libérale",
    desc: "Abattement de 34 %. Retraite gérée par la Sécurité Sociale des Indépendants.",
  },
};

const ERROR_MESSAGES = {
  validation_failed: "Certaines informations sont incomplètes. Vérifiez chaque étape avant de soumettre.",
  subscription_already_exists: "Votre organisation est déjà active — vous ne pouvez pas relancer le parcours de création.",
  awaiting_deposit_invalid_state: "Une formalité existe déjà pour cette organisation. Contactez le support.",
  pj_upload_failed: "Échec du téléversement de la pièce. Vérifiez le format (PDF) et la taille (≤ 10 Mo).",
  payment_provider_unavailable: "Notre prestataire de paiement est temporairement indisponible. Réessayez dans quelques instants.",
};

export default class DemarrageCreationController extends BaseController {
  static targets = [
    "submitSuccess",
    "submitError",
    "submitErrorMessage",
    "paymentLink",
    // PJ identity
    "pjStatusIdentity",
    "pjBtnIdentity",
    "pjFileIdentity",
    // PJ address
    "pjStatusAddress",
    "pjBtnAddress",
    "pjFileAddress",
    // PJ non-condemnation
    "pjStatusNonCondemnation",
    "pjBtnNonCondemnation",
    "pjFileNonCondemnation",
    // PJ RC Pro (facultative, catégorie « Autre » — présente dynamiquement)
    "pjStatusRcPro",
    "pjBtnRcPro",
    "pjFileRcPro",
    // PJ titre de séjour (conditionnelle, hors UE/EEE — 99.98)
    "pjStatusResidence",
    "pjBtnResidence",
    "pjFileResidence",
    "residencePjRow",
    // Collecte INPI étape 1/2 (99.98)
    "birthCountryRow",
    "birthCountrySelect",
    "nationalityCountryField",
    "nationalityCountrySelect",
    "businessAddressFieldset",
    // Cascade catégorisation d'activité étape 2 (99.98)
    "cat1",
    "cat2",
    "cat3",
    "cat4",
    // Consentements étape 5
    "consentMandate",
    "consentTerms",
    "consentRefund",
    // Quiz d'orientation de régime étape 3 (99.95)
    "regimeQuiz",
    "quizResultTitle",
    "quizResultDesc",
    "manualToggle",
    "manualRegime",
    "manualArtisanal",
    "manualPension",
  ];

  // Map<pjKey, attachment>
  _uploadedPjs = new Map();

  // EPIC-038 / 38.4 — tracker de conversion (funnel création INPI).
  // `_maxWizardStep` : index du panneau le plus avancé déjà signalé
  // (dédup + progression avant uniquement). `_submitSubscription` :
  // subscription renvoyée au submit, pour contextualiser `payment_initiated`.
  _maxWizardStep = 0;
  _wizardObserver = null;
  _submitSubscription = null;

  // Maps internes 99.98 : suggestions BAN par input, timers de debounce, arbre
  // de catégorisation INPI.
  _addressMaps = new Map();
  _debounceTimers = new Map();
  _catTree = null;

  connect() {
    this._trackWizardStarted();
    this._observeWizardSteps();
    if (this.hasPaymentLinkTarget) {
      this.paymentLinkTarget.addEventListener("click", () => this._trackPaymentInitiated());
    }
    // 99.98 — pré-remplissage des référentiels (pays de naissance, arbre de
    // catégorisation). Best-effort : un échec réseau n'empêche pas la saisie.
    if (this.hasBirthCountrySelectTarget) {
      this._fillCountryOptions(this.birthCountrySelectTarget, COUNTRIES);
    }
    this._loadActivityCategories();
  }

  disconnect() {
    this._wizardObserver?.disconnect();
    this._wizardObserver = null;
    for (const t of this._debounceTimers.values()) clearTimeout(t);
    this._debounceTimers.clear();
  }

  // ---------------------------------------------------------------------------
  // Étape 4 — Upload PJ (file picker handlers)
  // ---------------------------------------------------------------------------

  pickPjIdentity() { this._pickPj("identity"); }
  pickPjAddress() { this._pickPj("address"); }
  pickPjNonCondemnation() { this._pickPj("nonCondemnation"); }
  pickPjRcPro() { this._pickPj("rcPro"); }
  pickPjResidence() { this._pickPj("residence"); }

  uploadPjIdentity() { this._uploadPj("identity"); }
  uploadPjAddress() { this._uploadPj("address"); }
  uploadPjNonCondemnation() { this._uploadPj("nonCondemnation"); }
  uploadPjRcPro() { this._uploadPj("rcPro"); }
  uploadPjResidence() { this._uploadPj("residence"); }

  _pickPj(key) {
    const input = this[`pjFile${this._cap(key)}Target`];
    if (input) input.click();
  }

  async _uploadPj(key) {
    const input = this[`pjFile${this._cap(key)}Target`];
    const btn = this[`pjBtn${this._cap(key)}Target`];
    const status = this[`pjStatus${this._cap(key)}Target`];
    const file = input?.files?.[0];
    if (!file) return;

    // Validation locale rapide.
    if (file.size > 10 * 1024 * 1024) {
      this._setPjStatus(status, "error", "Le fichier dépasse 10 Mo.");
      return;
    }
    if (file.type && file.type !== "application/pdf") {
      this._setPjStatus(status, "error", "Seuls les fichiers PDF sont acceptés.");
      return;
    }

    if (btn) {
      btn.disabled = true;
      btn.textContent = "Téléversement…";
    }
    this._setPjStatus(status, "loading", "Envoi en cours…");

    try {
      const data = await api.inpi.attachmentUpload(file, PJ_API_TYPE[key]);
      const attachment = data?.attachment;
      if (!attachment) throw new Error("Réponse inattendue du serveur.");
      this._uploadedPjs.set(key, attachment);
      const sizeMb = (attachment.fileSizeBytes / (1024 * 1024)).toFixed(1);
      this._setPjStatus(status, "ok", `${attachment.filename || file.name} · ${sizeMb} Mo`);
      if (btn) btn.textContent = "Remplacer";
      this.showSuccess(`${PJ_LABEL[key]} téléversé.`);
    } catch (err) {
      console.error("PJ upload failed", err);
      const msg = this._extractMessage(err) || "Impossible de téléverser ce fichier.";
      this._setPjStatus(status, "error", msg);
      if (btn) btn.textContent = "Téléverser";
    } finally {
      if (btn) btn.disabled = false;
      if (input) input.value = "";
    }
  }

  _setPjStatus(statusEl, state, message) {
    if (!statusEl) return;
    statusEl.textContent = "";
    const span = document.createElement("span");
    if (state === "ok") {
      span.className = "nm-pill nm-pill--olive";
      const dot = document.createElement("span");
      dot.className = "nm-pill__dot";
      span.appendChild(dot);
      span.appendChild(document.createTextNode(message));
    } else if (state === "error") {
      span.className = "fs-12";
      span.style.color = "var(--error-text)";
      span.textContent = message;
    } else {
      span.className = "fs-12 text-subtle";
      span.textContent = message;
    }
    statusEl.appendChild(span);
  }

  // ---------------------------------------------------------------------------
  // Étape 5 — Submit
  // ---------------------------------------------------------------------------

  async submit(event) {
    if (event) event.preventDefault();
    this._clearSubmitFeedback();

    if (PJ_TYPES.some((key) => !this._uploadedPjs.has(key))) {
      this._showSubmitError(
        "Veuillez téléverser les 3 pièces obligatoires à l'étape 4 (identité, domicile, non-condamnation) avant de valider.",
      );
      return;
    }

    // Gate titre de séjour : obligatoire pour un ressortissant hors UE/EEE
    // (99.98). Le backend revalide ce gate.
    if (this._readInput("onb-nat") === "non_eu" && !this._uploadedPjs.has("residence")) {
      this._showSubmitError(
        "Veuillez téléverser votre titre de séjour à l'étape 4 (obligatoire pour un ressortissant hors UE/EEE).",
      );
      return;
    }

    // NIR optionnel mais, s'il est renseigné, sa clé doit être valide.
    const nirVal = this._readInput("onb-nir");
    if (nirVal !== "" && !this._isValidNir(nirVal)) {
      this._showSubmitError(
        "Le numéro de sécurité sociale (NIR) saisi à l'étape 1 est invalide. Corrigez-le ou laissez-le vide.",
      );
      return;
    }

    // Consentements obligatoires : mandat de dépôt INPI + CGU/CGV. Lecture
    // réelle des cases (99.94) — un codage en dur serait un défaut légal.
    if (!this._isConsentChecked("consentMandate") || !this._isConsentChecked("consentTerms")) {
      this._showSubmitError(
        "Veuillez accepter le mandat de dépôt INPI et les CGU/CGV à l'étape 5 avant de valider.",
      );
      return;
    }

    const submitBtn = document.querySelector("[data-wizard-submit]");
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = "Envoi du lien de paiement…";
    }

    const payload = this._buildPayload();
    try {
      const data = await api.inpi.creationSubmit(payload);
      const paymentLinkUrl = data?.subscription?.paymentLinkUrl || null;
      // EPIC-038 / 38.4 — funnel : dossier soumis. La subscription est
      // mémorisée pour contextualiser `payment_initiated` au clic du lien.
      this._submitSubscription = data?.subscription ?? null;
      window.NotamicroTracker?.track("inpi_wizard_submitted", {
        formalityId: data?.formality?.id ?? data?.formalityId ?? null,
      });
      this._showSubmitSuccess(paymentLinkUrl);
      // On masque le wizard pour focaliser sur le succès.
      const wizard = document.querySelector("[data-wizard]");
      if (wizard) wizard.hidden = true;
    } catch (err) {
      console.error("Submit failed", err);
      const msg = this._extractMessage(err) || "Impossible de soumettre votre dossier.";
      this._showSubmitError(msg);
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = "Valider et recevoir le lien de paiement par email →";
      }
    }
  }

  _buildPayload() {
    // Step 1 — identité (lecture par id stable des inputs maquette).
    const identity = {
      firstName: this._readInput("onb-prenom"),
      lastName: this._readInput("onb-nom"),
      birthDate: this._readInput("onb-naiss"),
    };
    const civility = this._readInput("onb-civilite");
    if (civility) identity.civility = civility;
    const usageName = this._readInput("onb-nomu");
    if (usageName) identity.usageName = usageName;
    const birthPlace = this._readInput("onb-naissL");
    if (birthPlace) identity.birthPlace = birthPlace;
    const nir = this._readInput("onb-nir");
    if (nir) identity.nir = nir;

    // Collecte INPI 99.98 — situation matrimoniale, nationalité ISO3, statut de
    // résidence, codes géographiques de naissance, pays de naissance.
    const maritalStatus = this._readInput("onb-situation");
    if (maritalStatus) identity.maritalStatus = maritalStatus;

    const residencyStatus = this._readInput("onb-nat"); // fr | eu | non_eu
    if (residencyStatus) identity.residencyStatus = residencyStatus;
    const nationalityCode = residencyStatus === "fr" ? "FRA" : this._readInput("onb-nat-pays");
    if (nationalityCode) identity.nationalityCode = nationalityCode;

    const birthInsee = this._readInput("onb-naissL-insee");
    if (birthInsee) identity.birthInseeCode = birthInsee;
    const birthCp = this._readInput("onb-naissL-cp");
    if (birthCp) identity.birthPostalCode = birthCp;
    if (this._isChecked("onb-naissL-etranger")) {
      const birthCountry = countryNameByIso3(this._readInput("onb-naissL-pays"));
      if (birthCountry) identity.birthCountry = birthCountry;
    }

    const personalAddress = {
      address: this._readInput("onb-adr"),
      postalCode: this._readInput("onb-cp"),
      city: this._readInput("onb-ville"),
      country: "FR",
    };
    const personalInsee = this._readInput("onb-adr-insee");
    if (personalInsee) personalAddress.inseeCode = personalInsee;

    // Adresse d'entreprise distincte (domiciliation = Oui, 99.98). Absente →
    // le backend retombe sur l'adresse personnelle (sameAsPersonal).
    let businessAddress = null;
    if (this._readInput("onb-domiciliation") === "oui") {
      businessAddress = {
        address: this._readInput("onb-siege-adr"),
        postalCode: this._readInput("onb-siege-cp"),
        city: this._readInput("onb-siege-ville"),
        country: "FR",
        sameAsPersonal: false,
      };
      const businessInsee = this._readInput("onb-siege-insee");
      if (businessInsee) businessAddress.inseeCode = businessInsee;
    }

    // Step 2 — activité principale.
    const isCommercialAgent = (this._readInput("onb-agent") || "non") === "oui";
    const activities = [{
      role: "principal",
      description: this._readInput("onb-desc"),
      nafCode: this._readInput("onb-naf"),
      regulatedCategory: this._readInput("onb-cat") || null,
      startDate: this._readInput("onb-debut"),
      isCommercialAgent,
    }];

    // Catégorisation officielle (codeFinal 8 chiffres) résolue par la cascade
    // guidée N1→N4 (99.98). Émise seulement si les 4 niveaux sont choisis.
    const categorisationCode = this._readInput("onb-categorisation-code");
    if (categorisationCode) activities[0].categorisationCode = categorisationCode;

    // Axes de régime résolus par le quiz d'orientation / la saisie manuelle
    // (99.95). Conditionnalité stricte calée sur la catégorie fiscale : pas
    // d'`isArtisanal` hors `bic_services`, pas de `pensionProvider` hors `bnc`.
    const taxCategory = this._readInput("onb-impot-cat") || null;
    if (taxCategory === "bic_services") {
      const artisanal = this._readInput("onb-impot-artisanal");
      if (artisanal === "true" || artisanal === "false") {
        activities[0].isArtisanal = artisanal === "true";
      }
    }
    if (taxCategory === "bnc") {
      const pension = this._readInput("onb-impot-pension");
      if (pension === "cipav" || pension === "ssi") {
        activities[0].pensionProvider = pension;
      }
    }

    // Step 3 — options fiscales (99.94). `urssafPeriodicity` est une config
    // Notamicro/URSSAF appliquée à l'activation, pas un champ du volet fiscal
    // INPI — voir la note côté backend (sanitizePayload).
    const fiscalOptions = {
      taxCategory,
      vfl: this._readInput("onb-vfl") || null,
      urssafPeriodicity: this._readInput("onb-periodicite") || null,
      vatRegime: this._readInput("onb-tva") || null,
    };

    // Step 4 — attachments uploadés (obligatoires + RC Pro facultative).
    const attachments = [];
    for (const [key, att] of this._uploadedPjs) {
      if (att && PJ_API_TYPE[key]) {
        attachments.push({
          type: PJ_API_TYPE[key],
          attachmentId: att.id,
        });
      }
    }

    const payload = {
      identity,
      personalAddress,
      activities,
      fiscalOptions,
      attachments,
      consentMandataire: this._isConsentChecked("consentMandate"),
      consentTransparency: this._isConsentChecked("consentTerms"),
      consentRefundPolicy: this._isConsentChecked("consentRefund"),
      // Consentement RGPD à la diffusion commerciale (étape 5, 99.98) — défaut N.
      diffusionCommerciale: this._isChecked("onb-diffusion"),
    };
    if (businessAddress) payload.businessAddress = businessAddress;

    return payload;
  }

  /**
   * Lit l'état réel d'une case de consentement (99.94). Retourne `false` si
   * la cible n'existe pas (panneau non monté) — jamais de consentement par
   * défaut.
   */
  _isConsentChecked(targetName) {
    const cap = this._cap(targetName);
    if (!this[`has${cap}Target`]) return false;
    return this[`${targetName}Target`].checked === true;
  }

  _readInput(id) {
    const el = document.getElementById(id);
    if (!el) return "";
    return (el.value || "").toString().trim();
  }

  _showSubmitSuccess(paymentLinkUrl) {
    if (this.hasSubmitSuccessTarget) this.submitSuccessTarget.hidden = false;
    if (paymentLinkUrl && this.hasPaymentLinkTarget) {
      this.paymentLinkTarget.href = paymentLinkUrl;
      this.paymentLinkTarget.hidden = false;
    }
    this.submitSuccessTarget?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  _showSubmitError(message) {
    if (this.hasSubmitErrorTarget) this.submitErrorTarget.hidden = false;
    if (this.hasSubmitErrorMessageTarget) this.submitErrorMessageTarget.textContent = " " + message;
    this.submitErrorTarget?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  _clearSubmitFeedback() {
    if (this.hasSubmitErrorTarget) this.submitErrorTarget.hidden = true;
    if (this.hasSubmitErrorMessageTarget) this.submitErrorMessageTarget.textContent = "";
    if (this.hasSubmitSuccessTarget) this.submitSuccessTarget.hidden = true;
  }

  // ---------------------------------------------------------------------------
  // EPIC-038 / 38.4 — tracker de conversion (funnel création INPI)
  // ---------------------------------------------------------------------------

  /**
   * `inpi_wizard_started` — 1er affichage du wizard. Dédup `sessionStorage`
   * (la page se recharge intégralement au retour navigateur).
   */
  _trackWizardStarted() {
    try {
      const key = "nm-tracked-inpi_wizard_started";
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, "1");
    } catch (_e) {
      // sessionStorage indisponible — on émet quand même (sans dédup).
    }
    window.NotamicroTracker?.track("inpi_wizard_started");
  }

  /**
   * Observe le passage en `.is-active` des `[data-wizard-panel]` (la
   * navigation du wizard est en JS inline de page, EPIC-002). Émet
   * `inpi_wizard_step_completed { step }` une fois par étape, sur
   * progression avant uniquement — `step` = index 0-based du panneau
   * atteint, qui vaut le numéro 1-based de l'étape qui vient d'être
   * validée (panneau 0 = étape 1 ; la valider active le panneau 1).
   */
  _observeWizardSteps() {
    const panels = document.querySelectorAll("[data-wizard-panel]");
    if (panels.length === 0) return;

    this._wizardObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        const panel = mutation.target;
        if (!panel.classList.contains("is-active")) continue;
        const step = Number.parseInt(panel.dataset.wizardPanel, 10);
        if (Number.isNaN(step) || step <= this._maxWizardStep) continue;
        this._maxWizardStep = step;
        window.NotamicroTracker?.track("inpi_wizard_step_completed", { step });
      }
    });
    for (const panel of panels) {
      this._wizardObserver.observe(panel, { attributes: true, attributeFilter: ["class"] });
    }
  }

  /**
   * `payment_initiated` — clic sur le lien de paiement post-soumission.
   * Contexte best-effort depuis la subscription renvoyée au submit.
   */
  _trackPaymentInitiated() {
    const sub = this._submitSubscription;
    const context = {};
    if (sub?.amountHt != null) context.amount = sub.amountHt;
    if (sub?.cadence != null) context.plan = sub.cadence;
    window.NotamicroTracker?.track("payment_initiated", context);
  }

  // ---------------------------------------------------------------------------
  // Étape 3 — Quiz d'orientation de régime (99.95)
  // ---------------------------------------------------------------------------

  /** Navigation entre étapes du quiz (`data-quiz-goto`). */
  quizNav(event) {
    const target = event.currentTarget?.dataset?.quizGoto;
    if (target) this._showQuizStep(target);
  }

  /**
   * Résolution terminale : pose `category` (+ axe conditionnel), affiche le
   * récap du régime identifié et révèle l'étape résultat.
   */
  quizResolve(event) {
    const ds = event.currentTarget?.dataset ?? {};
    const category = ds.quizCategory ?? "";
    const artisanal = ds.quizArtisanal ?? "";
    const pension = ds.quizPension ?? "";
    this._setRegime(category, artisanal, pension);

    const recap = REGIME_RECAP[this._regimeKey(category, artisanal, pension)];
    if (recap) {
      if (this.hasQuizResultTitleTarget) this.quizResultTitleTarget.textContent = recap.title;
      if (this.hasQuizResultDescTarget) this.quizResultDescTarget.textContent = recap.desc;
    }
    this._showQuizStep("result");
  }

  /** Relance le quiz depuis la première question et efface le régime saisi. */
  quizRestart() {
    this._setRegime("", "", "");
    this._showQuizStep("q1");
  }

  /**
   * Échappatoire « Je le choisis moi-même » : masque le quiz, révèle le
   * `<select>` et synchronise les axes conditionnels avec sa valeur.
   */
  revealManualRegime() {
    if (this.hasRegimeQuizTarget) this.regimeQuizTarget.hidden = true;
    if (this.hasManualToggleTarget) this.manualToggleTarget.closest(".nm-field__hint")?.setAttribute("hidden", "");
    if (this.hasManualRegimeTarget) {
      this.manualRegimeTarget.hidden = false;
      const select = document.getElementById("onb-impot-cat");
      this.onRegimeSelectChange();
      select?.focus();
    }
  }

  /** Toggle des axes conditionnels selon la catégorie choisie dans le select. */
  onRegimeSelectChange() {
    const category = this._readInput("onb-impot-cat");
    if (this.hasManualArtisanalTarget) this.manualArtisanalTarget.hidden = category !== "bic_services";
    if (this.hasManualPensionTarget) this.manualPensionTarget.hidden = category !== "bnc";
    this.syncManualAxes();
  }

  /**
   * Recopie les axes conditionnels (saisie manuelle) vers les champs cachés
   * autoritatifs, en respectant la conditionnalité (jamais d'axe orphelin).
   */
  syncManualAxes() {
    const category = this._readInput("onb-impot-cat");
    const artisanalInput = document.getElementById("onb-impot-artisanal");
    const pensionInput = document.getElementById("onb-impot-pension");
    if (artisanalInput) {
      artisanalInput.value = category === "bic_services"
        ? (document.getElementById("onb-impot-artisanal-select")?.value ?? "")
        : "";
    }
    if (pensionInput) {
      pensionInput.value = category === "bnc"
        ? (document.getElementById("onb-impot-pension-select")?.value ?? "")
        : "";
    }
  }

  /** Affiche une seule étape du quiz (`data-quiz-step`). */
  _showQuizStep(stepId) {
    if (!this.hasRegimeQuizTarget) return;
    const steps = this.regimeQuizTarget.querySelectorAll("[data-quiz-step]");
    for (const step of steps) {
      step.classList.toggle("is-active", step.dataset.quizStep === stepId);
    }
  }

  /**
   * Pose le régime dans le `<select>` autoritatif + les champs cachés
   * (`is_artisanal` / `pension_provider`), avec conditionnalité stricte. Un
   * `change` est dispatché pour que le récapitulatif inline de l'étape 5 se
   * mette à jour.
   */
  _setRegime(category, artisanal, pension) {
    const select = document.getElementById("onb-impot-cat");
    if (select) {
      select.value = category || "";
      select.dispatchEvent(new Event("change", { bubbles: true }));
    }
    const artisanalInput = document.getElementById("onb-impot-artisanal");
    if (artisanalInput) {
      artisanalInput.value = category === "bic_services" && (artisanal === "true" || artisanal === "false")
        ? artisanal
        : "";
    }
    const pensionInput = document.getElementById("onb-impot-pension");
    if (pensionInput) {
      pensionInput.value = category === "bnc" && (pension === "cipav" || pension === "ssi")
        ? pension
        : "";
    }
  }

  /** Clé de régime pour {@link REGIME_RECAP}. */
  _regimeKey(category, artisanal, pension) {
    if (category === "bic_services") {
      return artisanal === "true" ? "bic_services_artisanal" : "bic_services_commercial";
    }
    if (category === "bnc") {
      return pension === "cipav" ? "bnc_cipav" : "bnc_ssi";
    }
    return category;
  }

  // ---------------------------------------------------------------------------
  // Étape 1/2 — Collecte INPI 99.98 (nationalité, naissance, INSEE, catégorisation)
  // ---------------------------------------------------------------------------

  /** Révèle le pays de nationalité (UE/EEE ou hors) + la PJ titre de séjour. */
  onNationalityChange() {
    const val = this._readInput("onb-nat");
    const showCountry = val === "eu" || val === "non_eu";
    if (this.hasNationalityCountryFieldTarget) this.nationalityCountryFieldTarget.hidden = !showCountry;
    if (showCountry && this.hasNationalityCountrySelectTarget) {
      const list = val === "eu"
        ? EU_EEA_COUNTRIES.filter((c) => c.iso3 !== "FRA")
        : COUNTRIES.filter((c) => !c.eu);
      this._fillCountryOptions(this.nationalityCountrySelectTarget, list);
    }
    if (this.hasResidencePjRowTarget) this.residencePjRowTarget.hidden = val !== "non_eu";
  }

  /** Bascule commune FR (BAN) ↔ pays de naissance étranger (saisie manuelle). */
  toggleBirthAbroad() {
    const abroad = this._isChecked("onb-naissL-etranger");
    if (this.hasBirthCountryRowTarget) this.birthCountryRowTarget.hidden = !abroad;
    if (abroad) {
      this._setInputValue("onb-naissL-insee", "");
      this._setInputValue("onb-naissL-cp", "");
      this._clearDatalist("onb-naissL-list");
    }
  }

  /** Autocomplete BAN de la commune de naissance (communes FR uniquement). */
  onBirthPlaceInput(event) {
    const input = event.currentTarget;
    if (this._isChecked("onb-naissL-etranger")) return;
    const map = this._addressMaps.get(input.id);
    if (map && map.has(input.value)) {
      const picked = map.get(input.value);
      this._setInputValue("onb-naissL-insee", picked.citycode);
      this._setInputValue("onb-naissL-cp", picked.postcode);
      if (picked.city) input.value = picked.city;
      return;
    }
    this._debounce(`ban-${input.id}`, () => this._fetchBan(input, "municipality"), 250);
  }

  /** Autocomplete BAN d'une adresse (perso ou entreprise) via data-attributs. */
  onAddressInput(event) {
    const input = event.currentTarget;
    const map = this._addressMaps.get(input.id);
    if (map && map.has(input.value)) {
      const picked = map.get(input.value);
      this._setInputValue(input.dataset.nmCp, picked.postcode);
      this._setInputValue(input.dataset.nmCity, picked.city);
      this._setInputValue(input.dataset.nmInsee, picked.citycode);
      if (picked.name) input.value = picked.name;
      return;
    }
    this._debounce(`ban-${input.id}`, () => this._fetchBan(input, ""), 250);
  }

  /** Toggle de l'adresse d'entreprise distincte (domiciliation = Oui). */
  toggleBusinessAddress() {
    const show = this._readInput("onb-domiciliation") === "oui";
    if (this.hasBusinessAddressFieldsetTarget) this.businessAddressFieldsetTarget.hidden = !show;
  }

  /** Validation NIR (format + clé 97) au blur — optionnel mais valide si saisi. */
  validateNir() {
    const input = document.getElementById("onb-nir");
    const err = document.getElementById("onb-nir-error");
    if (!input || !err) return;
    const raw = (input.value || "").trim();
    if (raw === "") {
      err.hidden = true;
      err.textContent = "";
      input.removeAttribute("aria-invalid");
      return;
    }
    const ok = this._isValidNir(raw);
    err.hidden = ok;
    err.textContent = ok ? "" : "Numéro de sécurité sociale invalide — vérifiez les 15 chiffres et la clé de contrôle.";
    if (ok) input.removeAttribute("aria-invalid");
    else input.setAttribute("aria-invalid", "true");
  }

  // --- Cascade catégorisation d'activité (référentiel INPI N1→N4) ---

  async _loadActivityCategories() {
    if (!this.hasCat1Target) return;
    try {
      const data = await api.inpi.activityCategories();
      this._catTree = Array.isArray(data?.categories) ? data.categories : [];
      this._populateCatLevel(this.cat1Target, this._catTree, "codeN1", "libelleN1");
    } catch (_e) {
      // Référentiel indisponible — le conseiller complétera la catégorisation.
    }
  }

  onCatN1Change() {
    this._resetCatSelects([this.cat2Target, this.cat3Target, this.cat4Target]);
    const n1 = this.cat1Target.value;
    if (n1 && this._catTree) {
      this._populateCatLevel(this.cat2Target, this._catTree.filter((r) => r.codeN1 === n1), "codeN2", "libelleN2");
      this.cat2Target.disabled = false;
    }
    this._syncCatCode();
  }

  onCatN2Change() {
    this._resetCatSelects([this.cat3Target, this.cat4Target]);
    const n1 = this.cat1Target.value;
    const n2 = this.cat2Target.value;
    if (n1 && n2 && this._catTree) {
      this._populateCatLevel(this.cat3Target, this._catTree.filter((r) => r.codeN1 === n1 && r.codeN2 === n2), "codeN3", "libelleN3");
      this.cat3Target.disabled = false;
    }
    this._syncCatCode();
  }

  onCatN3Change() {
    this._resetCatSelects([this.cat4Target]);
    const n1 = this.cat1Target.value;
    const n2 = this.cat2Target.value;
    const n3 = this.cat3Target.value;
    if (n1 && n2 && n3 && this._catTree) {
      this._populateCatLevel(this.cat4Target, this._catTree.filter((r) => r.codeN1 === n1 && r.codeN2 === n2 && r.codeN3 === n3), "codeN4", "libelleN4");
      this.cat4Target.disabled = false;
    }
    this._syncCatCode();
  }

  onCatN4Change() {
    this._syncCatCode();
  }

  /** Recompose le codeFinal 8 chiffres et le valide contre l'arbre vendoré. */
  _syncCatCode() {
    const hidden = document.getElementById("onb-categorisation-code");
    if (!hidden) return;
    const n1 = this.cat1Target?.value;
    const n2 = this.cat2Target?.value;
    const n3 = this.cat3Target?.value;
    const n4 = this.cat4Target?.value;
    if (n1 && n2 && n3 && n4) {
      const code = `${n1}${n2}${n3}${n4}`;
      hidden.value = (this._catTree || []).some((r) => r.codeFinal === code) ? code : "";
    } else {
      hidden.value = "";
    }
  }

  // --- Helpers 99.98 ---

  /** Remplit un `<select>` pays (valeur = ISO3) en conservant le placeholder. */
  _fillCountryOptions(select, list) {
    if (!select) return;
    while (select.options.length > 1) select.remove(1);
    select.selectedIndex = 0;
    const frag = document.createDocumentFragment();
    for (const c of list) {
      const opt = document.createElement("option");
      opt.value = c.iso3;
      opt.textContent = c.name;
      frag.appendChild(opt);
    }
    select.appendChild(frag);
  }

  /** Peuple un niveau de cascade depuis les lignes filtrées (dédup par code). */
  _populateCatLevel(select, rows, codeKey, labelKey) {
    if (!select) return;
    const seen = new Map();
    for (const r of rows) {
      if (r[codeKey] && !seen.has(r[codeKey])) seen.set(r[codeKey], r[labelKey]);
    }
    while (select.options.length > 1) select.remove(1);
    select.selectedIndex = 0;
    const frag = document.createDocumentFragment();
    for (const [code, label] of seen) {
      const opt = document.createElement("option");
      opt.value = code;
      opt.textContent = label;
      frag.appendChild(opt);
    }
    select.appendChild(frag);
  }

  /** Vide + désactive les niveaux de cascade en aval. */
  _resetCatSelects(selects) {
    for (const s of selects) {
      if (!s) continue;
      while (s.options.length > 1) s.remove(1);
      s.selectedIndex = 0;
      s.disabled = true;
    }
  }

  /**
   * Requête BAN (api-adresse.data.gouv.fr) — peuple le `<datalist>` de l'input
   * et mémorise les métadonnées (INSEE, CP, ville) par label proposé. Échec
   * réseau silencieux : la saisie manuelle reste possible.
   */
  async _fetchBan(input, type) {
    const q = (input.value || "").trim();
    const listId = input.getAttribute("list");
    if (q.length < 3 || !listId) return;
    const typeParam = type ? `&type=${type}` : "";
    try {
      const url = `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(q)}&limit=6&autocomplete=1${typeParam}`;
      const res = await fetch(url, { headers: { Accept: "application/json" } });
      if (!res.ok) return;
      const data = await res.json();
      const features = Array.isArray(data?.features) ? data.features : [];
      const list = document.getElementById(listId);
      if (!list) return;
      list.innerHTML = "";
      const map = new Map();
      for (const f of features) {
        const p = f.properties || {};
        const city = p.city || p.name || "";
        const label = type === "municipality"
          ? `${city}${p.postcode ? ` (${p.postcode})` : ""}`
          : (p.label || "");
        if (!label) continue;
        const opt = document.createElement("option");
        opt.value = label;
        list.appendChild(opt);
        map.set(label, {
          name: p.name || label,
          city,
          postcode: p.postcode || "",
          citycode: p.citycode || "",
        });
      }
      this._addressMaps.set(input.id, map);
    } catch (_e) {
      // BAN indisponible — saisie manuelle conservée.
    }
  }

  /** Validation NIR français (15 chiffres, 2A/2B corses, clé 97). */
  _isValidNir(nir) {
    const n = (nir || "").toUpperCase().replace(/\s+/g, "");
    const m = n.match(/^([12])(\d{2})(\d{2})(\d{2}|2[AB])(\d{3})(\d{3})(\d{2})$/);
    if (!m) return false;
    let body = m[1] + m[2] + m[3] + m[4] + m[5] + m[6];
    body = body.replace("2A", "19").replace("2B", "18");
    if (!/^\d+$/.test(body)) return false;
    const key = Number.parseInt(m[7], 10);
    return key === 97 - (Number(body) % 97);
  }

  _isChecked(id) {
    const el = document.getElementById(id);
    return el ? el.checked === true : false;
  }

  _setInputValue(id, value) {
    if (!id) return;
    const el = document.getElementById(id);
    if (el) el.value = value || "";
  }

  _clearDatalist(listId) {
    const list = document.getElementById(listId);
    if (list) list.innerHTML = "";
  }

  _debounce(key, fn, delay) {
    clearTimeout(this._debounceTimers.get(key));
    this._debounceTimers.set(key, setTimeout(fn, delay));
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  _cap(s) {
    return s.charAt(0).toUpperCase() + s.slice(1);
  }

  _extractMessage(err) {
    const code = err?.data?.error?.code;
    if (code && ERROR_MESSAGES[code]) return ERROR_MESSAGES[code];
    if (err?.data?.error?.message) return err.data.error.message;
    if (err?.message) return err.message;
    return null;
  }
}
